import {
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  paths,
  t,
  styles,
} from '@hh/global.js'

class HHDraggableSlider extends customElements.get('hh-slider') {
  static properties = {
    active: {
      reflect: true,
    },
    breakpoint: {
      type: Number,
    },
    iconTypeLeft: {
      type: String,
      attribute: 'icon-type-left',
    },
    iconTypeRight: {
      type: String,
      attribute: 'icon-type-right',
    },
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    vertical: {
      reflect: true,
    },
    pressed: {
      type: Boolean,
      reflect: true,
    },
    dots: {
      reflect: true,
    },
    _advance: {
      state: true,
    },
    _scrollLeft: {
      state: true,
    },
    _scrollDirection: {
      state: true,
    },
    _direction: {
      state: true,
    },
    _maxScrollReached: {
      state: true,
    },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()
  $arrows = createRef()

  constructor() {
    super()
    this.dragSpeed = 3
    this._scrollLeft = 0
    this.snappingTimeout = undefined
    this.iconTypeLeft = this.iconTypeRight = 'caret'
    this._maxScrollReached = false
  }

  connectedCallback() {
    super.connectedCallback()

    setTimeout(() => {
      this.addEventListener('keydown', this.handleTabMove)
      this.toggleDraggableEvents()

      window.addEventListener('resize', () => {
        this.handleResize()
      })

      this.handleResize()
    }, 0)
  }

  handleResize() {
    const items = this.querySelectorAll('[slot="item"]')
    let totalWidth = 0
    items.forEach((item) => {
      totalWidth += item.offsetWidth
    })

    if (totalWidth < this.$container.value.offsetWidth) {
      this.$arrows.value.classList.add('hide')
    } else {
      this.$arrows.value.classList.remove('hide')
    }
  }

  handleTabMove(event) {
    if (event.key.toLowerCase() === 'tab') {
      const target = event.target.closest('[slot="item"]')
      if (!target) return
      const slideIndex = [...target.parentElement.children].indexOf(target)
      if (slideIndex === -1) return
      this.move(null, slideIndex)
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.toggleDraggableEvents('remove')
    this.removeEventListener('keydown', this.handleTabMove)
  }

  toggleDraggableEvents(action = 'add') {
    if (!this.$container?.value) return

    const actionValue =
      action === 'add' ? 'addEventListener' : 'removeEventListener'
    this.$container.value[actionValue](
      'touchstart',
      this.clearTimeoutAndClasses.bind(this)
    )

    this.$container.value[actionValue](
      'mouseleave',
      this.handleMouseLeave.bind(this)
    )

    this.$container.value[actionValue](
      'mousedown',
      this.handleMouseDown.bind(this)
    )

    this.$container.value[actionValue](
      'mousemove',
      this.handleDragStart.bind(this)
    )

    this.$container.value[actionValue]('mouseup', this.handleDragEnd.bind(this))
  }

  clearTimeoutAndClasses() {
    clearTimeout(this.snappingTimeout)
    this.clearDragClasses()
  }

  handleMouseLeave() {
    this.pressed = false
    this.clearDragClasses()
  }

  handleMouseDown(event) {
    clearTimeout(this.snappingTimeout)
    this.pressed = true
    this.classList.add('has-deactivated-snap')
    this.startX = event.pageX - this.$container.value.offsetLeft
    this._scrollLeft = this.$container.value.scrollLeft
  }

  handleDragStart(event) {
    if (!this.pressed) return
    event.preventDefault()
    event.stopPropagation()
    const currentX = event.pageX - this.$container.value.offsetLeft
    const slideDistance = (currentX - this.startX) * this.dragSpeed
    this.toggleDragClass(slideDistance)

    this.$container.value.scrollTo({
      left: this._scrollLeft - slideDistance,
    })
  }

  handleDragEnd(event) {
    this.pressed = false
    const oldScrollLeft = this.$container.value.scrollLeft
    this.classList.remove('has-deactivated-snap')
    const newScrollLeft = this.$container.value.scrollLeft
    this.classList.add('has-deactivated-snap')
    this.$container.value.scrollLeft = oldScrollLeft

    this.$container.value.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    })
    this.snappingTimeout = setTimeout(this.clearDragClasses.bind(this), 400)
  }

  toggleDragClass(slideDistance) {
    const isDragging =
      (slideDistance < 0 && slideDistance < -50) ||
      (slideDistance > 0 && slideDistance > 50)
    this.classList.toggle('is-dragging', isDragging)
  }

  clearDragClasses() {
    this.classList.remove('has-deactivated-snap')
    this.classList.remove('is-dragging')
  }

  updateUI() {
    super.updateUI()

    // Disable next button if next scroll would exceed maximum
    if (this.$container.value) {
      const { scrollLeft, scrollWidth, clientWidth } = this.$container.value
      const maxScrollReached = scrollLeft + clientWidth >= scrollWidth - 2
      
      if (this._maxScrollReached !== maxScrollReached) {
        this._maxScrollReached = maxScrollReached
      }
    }
  }

  render() {
    return html`
      <div class="container" ${ref(this.$container)} role="list">
        <slot name="item"></slot>
      </div>
      <div class="arrows" part="arrows">
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class="button button--prev"
          part="button-prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide', 'Previous')}"
          ?disabled="${this._current === 1}"
        >
          <svg
            part="icon"
            width="12"
            height="27"
            viewBox="0 0 12 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M1 1.125L10.625 13.5L1 25.875" stroke="#2E2A2B"></path>
          </svg>
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class="button button--next"
          part="button-next"
          name="next"
          aria-label="${t('accessibility.next_slide', 'Next')}"
          ?disabled="${this._maxScrollReached}"
        >
          <svg
            part="icon"
            width="12"
            height="27"
            viewBox="0 0 12 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M1 1.125L10.625 13.5L1 25.875" stroke="#2E2A2B"></path>
          </svg>
        </button>
      </div>
      <div class="buttons" ${ref(this.$arrows)} part="buttons">
        <div class="counter">
          <span class="counter--current">${this._current}</span>
          <span aria-hidden="true"> / </span>
          <span class="visually-hidden">${t('accessibility.of', 'of')}</span>
          <span class="counter--total">${this._total ? this._total : ''}</span>
        </div>
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class="button button--prev"
          part="button-prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide', 'Previous')}"
          ?disabled="${this._current === 1}"
        >
          ${unsafeHTML(paths[this.iconTypeLeft])}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class="button button--next"
          part="button-next"
          name="next"
          aria-label="${t('accessibility.next_slide', 'Next')}"
          ?disabled="${this._maxScrollReached}"
        >
          ${unsafeHTML(paths[this.iconTypeRight])}
        </button>
      </div>
      ${this.renderDots()}
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      *,
      *::before,
      *::after {
        box-sizing: border-box;
      }

      :host {
        display: block;
        position: relative;
      }

      :host([active='true']) {
        --right-offset: 4.5rem;
        --left-offset: 1.5rem;
        padding-right: calc(var(--right-offset) - 1.5rem);
      }

      .container {
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        overflow-x: var(--slider-overflow, auto);
        scroll-snap-type: x mandatory;
        scrollbar-width: var(--scrollbar-width, none);
        -webkit-overflow-scrolling: auto;
        scroll-padding-left: var(--left-offset);
        padding-left: var(--right-offset);
        margin-inline: -0.2rem;
        padding-bottom: var(--hh-padding-bottom, 0);
        justify-content: var(--hds-container-justify-content, space-between);
        gap: var(--hds-container-gap);
      }

      :host([active='true']) .container {
        margin-inline: 0
          var(--custom-margin-inline, calc(-1 * var(--right-offset)));
        padding-left: var(--right-offset);
        padding-right: var(--right-offset);
      }

      :host([arrows='true']) .container {
        padding-right: var(--right-offset);
      }

      :host([active='true'].has-deactivated-snap) .container {
        scroll-snap-type: none;
      }

      :host([active='true'][vertical='true']) .container {
        aspect-ratio: var(--hh-vertical-aspect-ratio);
        flex-wrap: wrap;
        overflow-y: auto;
        overflow-x: hidden;
        scroll-snap-type: y mandatory;
      }

      ::slotted(*) {
        flex: 0 0 var(--hh-item-width, 50%);
        max-width: var(--hh-item-width, 50%);
        scroll-snap-align: start;
        padding-inline: 0;
      }

      :host([active='true']) ::slotted(*) {
        padding-inline: var(--hh-padding-inline, 0.4rem) 0;
      }

      :host([active='true']) ::slotted(:last-child) {
        margin-right: var(--right-offset);
      }

      :host([active='true']) .container::-webkit-scrollbar {
        height: 3px;
      }

      :host([active='true']) .container::-webkit-scrollbar-thumb {
        border: 0 solid rgba(0, 0, 0, 0);
        background-clip: padding-box;
        border-radius: 9999px;
        background-color: #626262;
      }

      :host([active='true']) .container::-webkit-scrollbar-track {
        border: 0;
        background: var(--hh-scrollbar-track-color, #dad5ce);
      }

      @media screen and (min-width: 767px) {
        :host([active='true']) {
          --right-offset: 6rem;
          --left-offset: 6rem;
          padding-right: 0;
        }

        ::slotted(*) {
          flex: 0 0 var(--hh-item-width, 33.33%);
          max-width: var(--hh-item-width, 33.33%);
        }

        .arrows {
          position: absolute;
          top: 45%;
          right: 0;
          width: 100%;
          justify-content: space-between;
          margin: auto;
          align-items: center;
          pointer-events: none;
          transition: all 0.3s ease;
        }

        .arrows .button--prev {
          transform: rotate(180deg);
          pointer-events: all;
        }

        .arrows .button:disabled {
          opacity: 0;
        }

        .arrows .button--next {
          pointer-events: all;
          transform: none;
        }

        .arrows .button svg {
          height: 3.4rem;
        }

        :host([active='true'][buttons='true']) .buttons {
          display: flex;
          height: max-content;
        }

        :host([active='true'][arrows='true']) .arrows {
          display: flex;
          z-index: 100;
        }
      }

      .arrows {
        display: none;
      }

      .buttons {
        display: var(--hds-buttons-display, none);
        align-items: center;
        position: absolute;
        z-index: 2;
        right: var(--hds-buttons-right, 0);
        bottom: 0;
        top: var(--hds-buttons-top, initial);
        border: var(--hh-button-border);
        background: var(--hh-button-background);
      }

      .buttons.hide {
        display: none !important;
      }

      .dots {
        display: none;
        position: absolute;
        z-index: 3;
        bottom: 20px;
        left: 0;
        right: 0;
      }

      :host([vertical='true']) .dots {
        top: 50%;
        bottom: auto;
        right: auto;
        left: 10px;
        transform: translateY(-50%);
      }

      :host([active='true'][dots='true']) .dots {
        display: block;
      }

      .dots ul {
        list-style-type: none;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin: 0;
      }

      :host([vertical='true']) .dots ul {
        display: block;
      }

      .dots li {
        margin: 0 5px;
      }

      :host([vertical='true']) .dots li {
        margin: 5px 0;
      }

      .dots button {
        background: none;
        width: 5px;
        height: 5px;
        padding: 0;
        border: 1px solid var(--hh-color-brand-tertiary);
        border-radius: 0;
        position: relative;
        z-index: 1;
      }

      .dots button.is-current {
        background: var(--hh-color-brand-tertiary);
        border-radius: 50%;
      }

      .counter {
        margin: 0 2rem;
        display: var(--hds-counter-display, initial);
      }

      .button {
        width: var(--hh-button-width, 44px);
        height: var(--hh-button-height, 44px);
        cursor: pointer;
        color: var(--hh-color);
        border: none;
        background: var(--hh-button-background);
        padding: var(--hh-button-padding, revert);
      }

      .button[disabled] {
        color: var(--hds-button-disabled);
      }

      .button svg {
        height: var(--hh-button-svg-height, 0.6rem);
        width: var(--hh-button-svg-width, auto);
        pointer-events: none;
      }

      .button--prev {
        margin-left: -0.2rem;
        transform: var(--hds-button-prev-transform, rotate(90deg));
      }

      .button--next {
        margin-left: var(--hds-button-margin-left, -0.2rem);
        transform: var(--hds-button-next-transform, rotate(-90deg));
      }
    `,
  ]
}

customElements.define('hh-draggable-slider', HHDraggableSlider)
